import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { useToast } from '../../components/ui/Toast';
import { money } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader } from '../../components/ui';
import { Drawer, Field, NumberInput, PercentInput, Select, TextInput } from '../../components/ui/Form';

interface Scheme {
  id: string;
  code: string;
  name: string;
  scheme_type: 'AMOUNT' | 'WEIGHT';
  period: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  instalments: number;
  instalment_amount: string;
  flexible: boolean;
  min_amount: string;
  max_amount: string | null;
  bonus_mode: 'NONE' | 'PERCENT' | 'ONE_INSTALMENT' | 'FIXED';
  bonus_value: string;
  wastage_discount_pct: string;
  making_discount_pct: string;
  grace_days: number;
  break_penalty_pct: string;
  metal_id: string | null;
  purity_id: string | null;
  purity_code: string | null;
  allow_online: boolean;
  details: string | null;
  status: 'ACTIVE' | 'CLOSED';
  members: number;
  active_members: number;
}

interface Form {
  code: string;
  name: string;
  schemeType: 'AMOUNT' | 'WEIGHT';
  period: 'DAILY' | 'WEEKLY' | 'MONTHLY';
  instalments: string;
  instalmentAmount: string;
  flexible: boolean;
  minAmount: string;
  maxAmount: string;
  bonusMode: 'NONE' | 'PERCENT' | 'ONE_INSTALMENT' | 'FIXED';
  bonusValue: string;
  wastageDiscountPct: string;
  makingDiscountPct: string;
  graceDays: string;
  breakPenaltyPct: string;
  metalId: string;
  purityId: string;
  allowOnline: boolean;
  details: string;
  status: 'ACTIVE' | 'CLOSED';
}

const blank: Form = {
  code: '',
  name: '',
  schemeType: 'AMOUNT',
  period: 'MONTHLY',
  instalments: '11',
  instalmentAmount: '',
  flexible: false,
  minAmount: '',
  maxAmount: '',
  bonusMode: 'ONE_INSTALMENT',
  bonusValue: '',
  wastageDiscountPct: '',
  makingDiscountPct: '',
  graceDays: '7',
  breakPenaltyPct: '',
  metalId: '',
  purityId: '',
  allowOnline: false,
  details: '',
  status: 'ACTIVE',
};

const fromScheme = (s: Scheme): Form => ({
  code: s.code,
  name: s.name,
  schemeType: s.scheme_type,
  period: s.period,
  instalments: String(s.instalments),
  instalmentAmount: Number(s.instalment_amount) > 0 ? s.instalment_amount : '',
  flexible: s.flexible,
  minAmount: Number(s.min_amount) > 0 ? s.min_amount : '',
  maxAmount: s.max_amount ?? '',
  bonusMode: s.bonus_mode,
  bonusValue: Number(s.bonus_value) > 0 ? s.bonus_value : '',
  wastageDiscountPct: Number(s.wastage_discount_pct) > 0 ? s.wastage_discount_pct : '',
  makingDiscountPct: Number(s.making_discount_pct) > 0 ? s.making_discount_pct : '',
  graceDays: String(s.grace_days),
  breakPenaltyPct: Number(s.break_penalty_pct) > 0 ? s.break_penalty_pct : '',
  metalId: s.metal_id ?? '',
  purityId: s.purity_id ?? '',
  allowOnline: s.allow_online,
  details: s.details ?? '',
  status: s.status,
});

const bonusText = (s: Scheme): string =>
  s.bonus_mode === 'PERCENT'
    ? `${s.bonus_value}% bonus`
    : s.bonus_mode === 'ONE_INSTALMENT'
      ? `${s.instalments} + 1 free`
      : s.bonus_mode === 'FIXED'
        ? `${money(s.bonus_value)} bonus`
        : 'No bonus';

/**
 * The scheme master: the savings plans a shop offers.
 *
 * Terms here apply to people who join from now on. Anyone already enrolled
 * keeps the amount and schedule they signed up to, so changing a scheme never
 * quietly changes a customer's promise.
 */
export function ChitSchemesPage() {
  const { can } = usePermission();
  const navigate = useNavigate();
  const [editing, setEditing] = useState<Scheme | 'new' | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['chit-schemes'],
    queryFn: () => api.get<{ data: Scheme[] }>('/chit/schemes'),
  });
  const rows = data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Savings schemes"
        subtitle="The plans customers can join. Changes apply to new members; existing ones keep their terms."
        actions={
          <>
            <button className="btn-ghost" onClick={() => navigate('/chits')}>
              Gold savings
            </button>
            {can('chit.scheme') ? (
              <button className="btn-primary" onClick={() => setEditing('new')}>
                New scheme
              </button>
            ) : null}
          </>
        }
      />
      <ErrorNotice error={error} />
      {isLoading ? <Loading rows={4} /> : null}
      {!isLoading && rows.length === 0 ? (
        <Empty
          message="No schemes yet."
          hint="A typical one is 11 monthly instalments with the 12th paid by the shop. Weight schemes save in grams instead of rupees, protecting the customer when gold rises."
          action={can('chit.scheme') ? { label: 'Create a scheme', onClick: () => setEditing('new') } : undefined}
        />
      ) : null}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {rows.map((s) => (
          <button
            key={s.id}
            onClick={() => can('chit.scheme') && setEditing(s)}
            className={`rounded-xl border bg-surface p-4 text-left shadow-card transition-shadow hover:shadow-pop ${
              s.status === 'ACTIVE' ? 'border-silver-100' : 'border-dashed border-silver-400 opacity-70'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-base font-semibold">{s.name}</p>
                <p className="num text-2xs text-ink-faint">{s.code}</p>
              </div>
              <span
                className={`rounded px-1.5 py-0.5 text-2xs font-medium ${
                  s.scheme_type === 'WEIGHT' ? 'bg-gold-100 text-gold-700' : 'bg-silver-100 text-ink-soft'
                }`}
              >
                {s.scheme_type === 'WEIGHT' ? `Gold ${s.purity_code ?? ''}` : 'Amount'}
              </span>
            </div>
            <p className="num mt-3 text-xl font-bold">
              {s.instalments} × {Number(s.instalment_amount) > 0 ? money(s.instalment_amount) : s.flexible ? 'flexible' : 'customer’s choice'}
            </p>
            <p className="text-sm text-ink-soft">
              {s.period.toLowerCase()} · {bonusText(s)}
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5 text-2xs">
              {Number(s.wastage_discount_pct) > 0 ? <Tag>{s.wastage_discount_pct}% off wastage</Tag> : null}
              {Number(s.making_discount_pct) > 0 ? <Tag>{s.making_discount_pct}% off making</Tag> : null}
              {Number(s.break_penalty_pct) > 0 ? <Tag>{s.break_penalty_pct}% if closed early</Tag> : null}
              {s.allow_online ? <Tag>online</Tag> : null}
            </div>
            <div className="mt-3 flex items-center justify-between border-t border-silver-100 pt-2 text-2xs text-ink-soft">
              <span>
                <span className="num font-semibold text-ink">{s.active_members}</span> active of{' '}
                <span className="num">{s.members}</span> members
              </span>
              <span className={s.status === 'ACTIVE' ? 'text-success' : ''}>{s.status === 'ACTIVE' ? 'Open to join' : 'Closed'}</span>
            </div>
          </button>
        ))}
      </div>

      {editing ? <SchemeDrawer scheme={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </>
  );
}

function Tag({ children }: { children: React.ReactNode }) {
  return <span className="rounded-full bg-gold-50 px-2 py-0.5 text-gold-700">{children}</span>;
}

function SchemeDrawer({ scheme, onClose }: { scheme: Scheme | null; onClose: () => void }) {
  const client = useQueryClient();
  const toast = useToast();
  const onEnter = useKeyboardForm();
  const [f, setF] = useState<Form>(scheme ? fromScheme(scheme) : blank);
  const [error, setError] = useState<unknown>(null);
  const set = (patch: Partial<Form>): void => setF({ ...f, ...patch });

  const metals = useQuery({ queryKey: ['metals'], queryFn: () => api.get<{ data: { id: string; code: string; name: string }[] }>('/metals') });
  const purities = useQuery({
    queryKey: ['purities'],
    queryFn: () => api.get<{ data: { id: string; code: string; metal_id: string }[] }>('/purities?limit=100'),
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        code: f.code.trim(),
        name: f.name.trim(),
        schemeType: f.schemeType,
        period: f.period,
        instalments: Number(f.instalments),
        instalmentAmount: f.instalmentAmount ? Number(f.instalmentAmount).toFixed(2) : '0.00',
        flexible: f.flexible,
        minAmount: f.minAmount ? Number(f.minAmount).toFixed(2) : '0.00',
        maxAmount: f.maxAmount ? Number(f.maxAmount).toFixed(2) : null,
        bonusMode: f.bonusMode,
        bonusValue: f.bonusValue ? Number(f.bonusValue).toFixed(2) : '0.00',
        wastageDiscountPct: f.wastageDiscountPct || '0',
        makingDiscountPct: f.makingDiscountPct || '0',
        graceDays: Number(f.graceDays || 0),
        breakPenaltyPct: f.breakPenaltyPct || '0',
        metalId: f.schemeType === 'WEIGHT' ? f.metalId : null,
        purityId: f.schemeType === 'WEIGHT' ? f.purityId : null,
        allowOnline: f.allowOnline,
        details: f.details.trim() || null,
        status: f.status,
      };
      return scheme ? api.put(`/chit/schemes/${scheme.id}`, body) : api.post('/chit/schemes', body);
    },
    onSuccess: () => {
      toast(scheme ? `${f.name} updated` : `${f.name} created`);
      void client.invalidateQueries({ queryKey: ['chit-schemes'] });
      onClose();
    },
    onError: setError,
  });

  const amt = Number(f.instalmentAmount || 0);
  const n = Number(f.instalments || 0);
  const bonus =
    f.bonusMode === 'PERCENT' ? (amt * n * Number(f.bonusValue || 0)) / 100 : f.bonusMode === 'ONE_INSTALMENT' ? amt : f.bonusMode === 'FIXED' ? Number(f.bonusValue || 0) : 0;

  const ready =
    f.code.trim() &&
    f.name.trim() &&
    n >= 1 &&
    (f.schemeType === 'AMOUNT' || (f.metalId && f.purityId)) &&
    (!f.flexible || f.maxAmount);

  const purityOptions = (purities.data?.data ?? []).filter((p) => p.metal_id === f.metalId).map((p) => ({ value: p.id, label: p.code }));

  return (
    <Drawer
      title={scheme ? scheme.name : 'New scheme'}
      subtitle={scheme && scheme.members > 0 ? `${scheme.members} members keep the terms they joined on` : 'A savings plan customers can join'}
      onClose={onClose}
      footer={
        <button className="btn-primary w-full" disabled={!ready || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : scheme ? 'Save changes' : 'Create scheme'}
        </button>
      }
    >
      <ErrorNotice error={error} />
      <form autoComplete="off" onKeyDown={onEnter} onSubmit={(e) => e.preventDefault()} className="space-y-5">
        <section className="space-y-3">
          <div className="grid grid-cols-[1fr_120px] gap-3">
            <Field label="Scheme name" required>
              <TextInput value={f.name} onChange={(v) => set({ name: v })} placeholder="Ishwaryam 11+1" maxLength={120} autoFocus />
            </Field>
            <Field label="Code" required>
              <TextInput value={f.code} onChange={(v) => set({ code: v })} uppercase maxLength={32} allow={/[A-Z0-9\-_]/} />
            </Field>
          </div>

          <Field label="Scheme type" required>
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  ['AMOUNT', 'Amount', 'Saves rupees; redeemed as rupees plus bonus'],
                  ['WEIGHT', 'Gold weight', 'Each instalment buys grams at that day’s rate'],
                ] as const
              ).map(([v, label, hint]) => (
                <button
                  type="button"
                  key={v}
                  onClick={() => set({ schemeType: v })}
                  className={`rounded-lg border p-2.5 text-left ${f.schemeType === v ? 'border-gold-500 bg-gold-50' : 'border-silver-100'}`}
                >
                  <p className="text-sm font-medium">{label}</p>
                  <p className="text-2xs text-ink-soft">{hint}</p>
                </button>
              ))}
            </div>
          </Field>

          {f.schemeType === 'WEIGHT' ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Metal" required>
                <Select
                  value={f.metalId}
                  onChange={(v) => set({ metalId: v, purityId: '' })}
                  options={(metals.data?.data ?? []).map((m) => ({ value: m.id, label: m.name }))}
                />
              </Field>
              <Field label="Purity" required>
                <Select value={f.purityId} onChange={(v) => set({ purityId: v })} options={purityOptions} placeholder="Choose" />
              </Field>
            </div>
          ) : null}
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-gold-700">Instalments</h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Every">
              <Select
                value={f.period}
                onChange={(v) => set({ period: v as Form['period'] })}
                options={[
                  { value: 'MONTHLY', label: 'Month' },
                  { value: 'WEEKLY', label: 'Week' },
                  { value: 'DAILY', label: 'Day' },
                ]}
              />
            </Field>
            <Field label="Number of instalments" required>
              <NumberInput value={f.instalments} onChange={(v) => set({ instalments: v })} decimals={0} max={120} />
            </Field>
          </div>
          <Field label="Amount per instalment" hint="Leave blank to let each customer choose when they join.">
            <NumberInput value={f.instalmentAmount} onChange={(v) => set({ instalmentAmount: v })} decimals={2} placeholder="customer’s choice" />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.flexible} onChange={(e) => set({ flexible: e.target.checked })} />
            Flexible — each instalment may be a different amount
          </label>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Least per instalment">
              <NumberInput value={f.minAmount} onChange={(v) => set({ minAmount: v })} decimals={2} />
            </Field>
            <Field label="Most per instalment" required={f.flexible}>
              <NumberInput value={f.maxAmount} onChange={(v) => set({ maxAmount: v })} decimals={2} />
            </Field>
          </div>
          <Field label="Grace days" hint="How late a payment can be before it shows as overdue.">
            <NumberInput value={f.graceDays} onChange={(v) => set({ graceDays: v })} decimals={0} max={60} />
          </Field>
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-gold-700">Benefits</h3>
          <Field label="Bonus at maturity">
            <Select
              value={f.bonusMode}
              onChange={(v) => set({ bonusMode: v as Form['bonusMode'] })}
              options={[
                { value: 'ONE_INSTALMENT', label: 'One instalment free (11 + 1)' },
                { value: 'PERCENT', label: 'A percentage of savings' },
                { value: 'FIXED', label: 'A fixed amount' },
                { value: 'NONE', label: 'No bonus' },
              ]}
            />
          </Field>
          {f.bonusMode === 'PERCENT' || f.bonusMode === 'FIXED' ? (
            <Field label={f.bonusMode === 'PERCENT' ? 'Bonus %' : 'Bonus amount'}>
              {f.bonusMode === 'PERCENT' ? (
                <PercentInput value={f.bonusValue} onChange={(v) => set({ bonusValue: v })} />
              ) : (
                <NumberInput value={f.bonusValue} onChange={(v) => set({ bonusValue: v })} decimals={2} />
              )}
            </Field>
          ) : null}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Wastage discount %" hint="On the jewellery bought">
              <PercentInput value={f.wastageDiscountPct} onChange={(v) => set({ wastageDiscountPct: v })} />
            </Field>
            <Field label="Making discount %" hint="On the jewellery bought">
              <PercentInput value={f.makingDiscountPct} onChange={(v) => set({ makingDiscountPct: v })} />
            </Field>
          </div>
          <Field label="Deduction if closed early %" hint="Kept from the refund; no bonus is paid either.">
            <PercentInput value={f.breakPenaltyPct} onChange={(v) => set({ breakPenaltyPct: v })} />
          </Field>

          {amt > 0 && n > 0 ? (
            <div className="rounded-lg border border-gold-300 bg-gold-50 p-3 text-sm">
              <div className="flex justify-between">
                <span>
                  Customer pays {n} × {money(amt.toFixed(2))}
                </span>
                <span className="num">{money((amt * n).toFixed(2))}</span>
              </div>
              <div className="flex justify-between text-success">
                <span>Shop adds</span>
                <span className="num">+ {money(bonus.toFixed(2))}</span>
              </div>
              <div className="flex justify-between border-t border-gold-300 pt-1 font-semibold">
                <span>Customer spends</span>
                <span className="num">{money((amt * n + bonus).toFixed(2))}</span>
              </div>
            </div>
          ) : null}
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-gold-700">Other</h3>
          <Field label="Details for the customer">
            <textarea
              className="field min-h-[80px]"
              value={f.details}
              maxLength={2000}
              onChange={(e) => set({ details: e.target.value })}
              placeholder="Terms printed on the passbook"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.allowOnline} onChange={(e) => set({ allowOnline: e.target.checked })} />
            Allow online payments
          </label>
          {scheme ? (
            <Field label="Status">
              <Select
                value={f.status}
                onChange={(v) => set({ status: v as Form['status'] })}
                options={[
                  { value: 'ACTIVE', label: 'Open to new members' },
                  { value: 'CLOSED', label: 'Closed to new members' },
                ]}
              />
            </Field>
          ) : null}
        </section>
      </form>
    </Drawer>
  );
}
